import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/UserContextProvider";
import type { PresenceStatus } from "../components/learner/duels/friendTypes";

// Where the learner is → what their friends see. Coarse by design: the panel needs to know whether
// interrupting is rude, not which lesson they are on. First match wins.
const ROUTE_STATUS_RULES: { pattern: RegExp; status: PresenceStatus }[] = [
  // Duel lobby, match and spectate.
  { pattern: /^\/duels\/.+/, status: "IN_DUEL" },
  // A timed exam application — the one place an interruption actually costs marks.
  { pattern: /^\/standard-exam-applications\/.+/, status: "IN_EXAM" },
  { pattern: /^\/practise\/.*\/activities\/.+/, status: "SOLVING" },
  { pattern: /^\/branch-assignments\/.+/, status: "SOLVING" },
  { pattern: /^\/personalized-assignments\/.+/, status: "SOLVING" },
  { pattern: /^\/contests\/.+/, status: "SOLVING" },
];

export const statusForPathname = (pathname: string): PresenceStatus => {
  const matched = ROUTE_STATUS_RULES.find((rule) => rule.pattern.test(pathname));
  return matched ? matched.status : "ONLINE";
};

/**
 * Reports the learner's coarse presence status over the existing STOMP connection whenever it changes, so
 * friends see "Soru çözüyor" instead of a bare "Çevrimiçi" and the server can refuse a duel invite that
 * would interrupt them.
 *
 * Sends on CHANGE only. A learner clicking around the app crosses many routes that all map to ONLINE, and
 * each frame costs a server round-trip plus a fan-out to every online friend.
 *
 * It publishes through `webSocketStompClient` from the user context rather than calling `useWebSocket()`:
 * that hook owns the connection lifecycle and is instantiated exactly once in App.tsx — calling it again
 * here would open a second socket.
 */
const usePresenceStatusReporter = () => {
  const location = useLocation();
  const { webSocketStompClient, webSocketConnectionStatus, userProfile } = useAuth();
  const lastSentRef = useRef<PresenceStatus | null>(null);

  useEffect(() => {
    if (userProfile?.role !== "LEARNER") return;
    const status = statusForPathname(location.pathname);
    if (status === lastSentRef.current) return;
    if (!webSocketStompClient?.connected) {
      // Leave lastSentRef untouched so the reconnect effect below re-sends what we could not deliver.
      return;
    }
    try {
      webSocketStompClient.send(
        "/app/presence/status",
        {},
        JSON.stringify({ status }),
      );
      lastSentRef.current = status;
    } catch (error) {
      // Presence is a courtesy signal; a dropped frame must never break navigation.
    }
  }, [location.pathname, webSocketStompClient, userProfile]);

  useEffect(() => {
    if (webSocketConnectionStatus !== "connected") {
      lastSentRef.current = null;
      return;
    }
    // A fresh connection resets the stored status to ONLINE server-side, so only a non-ONLINE status needs
    // re-asserting — someone who reconnects mid-question must not read as idle.
    const status = statusForPathname(window.location.pathname);
    if (status === "ONLINE") {
      lastSentRef.current = "ONLINE";
      return;
    }
    // Both effects can fire on one reconnect — the disconnect cleared lastSentRef, and the client object
    // is replaced, so the pathname effect above re-runs in the same commit and has usually already
    // re-asserted this exact status. Read the same ref it writes so whichever runs first wins and the
    // other is a no-op, instead of two identical frames and two fan-outs per reconnect.
    if (status === lastSentRef.current) return;
    if (!webSocketStompClient?.connected) return;
    try {
      webSocketStompClient.send(
        "/app/presence/status",
        {},
        JSON.stringify({ status }),
      );
      lastSentRef.current = status;
    } catch (error) {
      // ignored, see above
    }
  }, [webSocketConnectionStatus, webSocketStompClient]);
};

export default usePresenceStatusReporter;
